import React, { useEffect, useState } from 'react';
import { GripVertical, Plus, Save, Trash2, Workflow } from 'lucide-react';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../config/settings-api';
import { slugifyFieldKey, getStatusButtonStyles } from '../../utils/orderConfigHelpers';
import './Settings.css';
import './OrderSettings.css';

const serializeStatuses = (statuses) =>
  JSON.stringify(
    (statuses || []).map((item) => ({
      label: item.label,
      value: item.value,
      color: item.color,
    }))
  );

const OrderStatusSettings = ({ statuses = [], canManage, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [showNewStatus, setShowNewStatus] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    const normalized = (statuses || []).map((item, index) => ({
      label: item.label || '',
      value: item.value || slugifyFieldKey(item.label),
      color: item.color || '#2563eb',
      position: item.position ?? index,
    }));
    setRows(normalized);
    setInitialSnapshot(serializeStatuses(normalized));
  }, [statuses]);

  const isChanged = serializeStatuses(rows) !== initialSnapshot;

  const handleAddStatus = () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error('Enter a status label');
      return;
    }
    const value = slugifyFieldKey(label);
    if (rows.some((row) => row.value === value)) {
      toast.error('This status already exists');
      return;
    }
    setRows((prev) => [...prev, { label, value, color: '#2563eb' }]);
    setNewLabel('');
    setShowNewStatus(false);
  };

  const moveRow = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= rows.length) return;
    setRows((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  };

  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    moveRow(dragIndex, index);
    setDragIndex(index);
  };
  const handleDragEnd = () => setDragIndex(null);

  const handleSave = async () => {
    if (!rows.length) {
      toast.error('At least one order status is required');
      return;
    }
    setSaving(true);
    try {
      const payload = rows.map((row, index) => ({
        label: row.label.trim(),
        value: row.value || slugifyFieldKey(row.label),
        color: row.color || '#2563eb',
        position: index,
      }));

      const result = await settingsAPI.updateOrderStatuses(payload);
      if (result.success) {
        toast.success('Order statuses saved');
        setInitialSnapshot(serializeStatuses(payload));
        onSaved?.();
      } else {
        toast.error(result.msg || result.error || 'Failed to save order statuses');
      }
    } catch {
      toast.error('Failed to save order statuses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-card order-settings-card">
      <div className="settings-card-header">
        <div className="settings-card-title">
          <Workflow size={20} />
          <div>
            <h2>Order Status</h2>
            <p>Configure status flow, colors, and order</p>
          </div>
        </div>
        {canManage && (
          <button type="button" className="settings-save-btn" onClick={handleSave} disabled={!isChanged || saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      <div className="order-settings-body">
        <div className="order-status-list">
          {rows.map((row, index) => (
            <div
              key={index}
              className={`order-status-row${dragIndex === index ? ' dragging' : ''}`}
              draggable={canManage}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              {canManage && (
                <span className="order-status-drag" title="Drag to reorder">
                  <GripVertical size={18} />
                </span>
              )}
              <div className="order-status-color-input-row">
                <input
                  type="color"
                  value={row.color || '#2563eb'}
                  disabled={!canManage}
                  onChange={(e) => {
                    const color = e.target.value;
                    setRows((prev) => prev.map((item, i) => (i === index ? { ...item, color } : item)));
                  }}
                />
                <input
                  type="text"
                  className="order-status-color-hex"
                  value={row.color || '#2563eb'}
                  disabled={!canManage}
                  placeholder="#2563eb"
                  onChange={(e) => {
                    const color = e.target.value;
                    setRows((prev) => prev.map((item, i) => (i === index ? { ...item, color } : item)));
                  }}
                />
              </div>
              <div className="order-status-main">
                <input
                  type="text"
                  value={row.label}
                  disabled={!canManage}
                  onChange={(e) => {
                    const label = e.target.value;
                    setRows((prev) => prev.map((item, i) => (
                      i === index ? { ...item, label, value: slugifyFieldKey(label) || item.value } : item
                    )));
                  }}
                />
                <span className="order-status-value">{row.value}</span>
              </div>
              <span
                className="order-status-preview"
                style={getStatusButtonStyles(row.color || '#2563eb')}
              >
                {row.label}
              </span>
              {canManage && rows.length > 1 && (
                <button
                  type="button"
                  className="order-settings-icon-btn danger"
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                  title="Delete status"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>

        {canManage && (
          <div className={`order-settings-add-row ${rows.length === 0 ? 'no-margin' : ''}`}>
            {showNewStatus ? (
              <div className="order-settings-new-field">
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Shipped"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
                />
                <button type="button" className="settings-inline-btn" onClick={handleAddStatus}>Add</button>
                <button type="button" className="settings-inline-btn muted" onClick={() => { setShowNewStatus(false); setNewLabel(''); }}>Cancel</button>
              </div>
            ) : (
              <button type="button" className="settings-inline-btn" onClick={() => setShowNewStatus(true)}>
                <Plus size={16} /> Add status
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default OrderStatusSettings;
